import { useContext, useEffect, useState } from "react";
import { alpha, Avatar, Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import { queryClient } from "../../App";
import CustomPinInput from "../../components/customPinInput";
import OtpResendButton from "../../components/otpResendButton";
import { resetOtpCooldownForPhone } from "../../utils/otpCooldownReset";
import useTenantBranding from "../../hooks/useTenantBranding";
import { navigatePostAuth } from "../../utils/postAuthNavigation";
import AuthLayout from "../../components/auth/AuthLayout";
import PrimaryButton from "../../components/auth/PrimaryButton";
import SecondaryButton from "../../components/auth/SecondaryButton";

function RequestOtp({ openDialog, userInput, setOpenDialog, cooldownHook }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const {
    setAuth,
    setPersist,
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    setAuthWithTenant,
  } = useContext(AuthContext);
  const tenantIdForOtp = currentTenant?.id || getTenantIdFromUrl();
  const searchParams = new URLSearchParams(location.search);
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdForOtp =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;
  const [requestOtp, setRequestOtp] = useState(false);
  const [inputValue, setInputValue] = useState();

  const { enqueueSnackbar } = useSnackbar();

  const {
    isFetching: refecthLoading,
    data: otpData,
    error: otpError,
  } = useQuery(
    ["refetch_otp", userInput, tenantIdForOtp, clientIdForOtp],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "login",
          phone: userInput,
          tenant_id: tenantIdForOtp,
          ...(clientIdForOtp && { client_id: clientIdForOtp }),
        },
      }),
    {
      onSuccess: () => {
        cooldownHook.startCooldown(); // Start cooldown after successful OTP request
        setRequestOtp(false);
      },
      onError: () => setRequestOtp(false),
      enabled: requestOtp,
    }
  );

  const { isLoading, mutate, error } = useMutation((auth) => {
    return axios.post("/login", auth, {
      withCredentials: true,
    });
  });

  // HELPERS
  const onSubmit = async (formData) => {
    // Include tenant_id in login credentials if available
    const tenantId = currentTenant?.id || getTenantIdFromUrl();
    const inputData = {
      ...formData,
      phone: userInput,
      ...(tenantId && { tenant_id: tenantId }),
    };

    setPersist(true);
    setRequestOtp(false);

    return mutate(inputData, {
      onSuccess: (data) => {
        // Reset OTP cooldowns for this specific phone number on successful login
        resetOtpCooldownForPhone(userInput);

        const authData = data?.data?.data;

        if (tenantId) setAuthWithTenant(authData, tenantId);
        else setAuth(authData);

        queryClient.clear();
        navigatePostAuth(navigate, { redirectUri, location });
      },
    });
  };

  useEffect(() => {
    if (otpData) {
      setRequestOtp(false);
    }
  }, [otpData]);
  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
    }
    queryClient.clear();
  }, [enqueueSnackbar, error]);
  useEffect(() => {
    if (otpError) {
      if (otpError?.response?.data?.error?.field_error?.length > 0) {
        otpError?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          otpError?.response?.data?.error?.message ||
          otpError?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
      setRequestOtp(false);
    }
  }, [enqueueSnackbar, otpError, setRequestOtp]);

  const handleVerify = (e) => {
    e.preventDefault();
    if (isLoading || refecthLoading || inputValue?.length !== 6) return;
    onSubmit({ otp: inputValue });
  };

  return (
    <AuthLayout
      title="Verify Your Phone"
      onSubmit={handleVerify}
      header={
        (tenantLogo || !hideDefaultBranding) && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Avatar
              sx={{
                width: { xs: "120px", sm: "140px" },
                height: { xs: "85px", sm: "100px" },
                borderRadius: 1,
              }}
              src={tenantLogo || logo}
            />
          </Box>
        )
      }
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1.5,
          }}
        >
          <SecondaryButton onClick={() => setOpenDialog(false)}>
            Back
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={isLoading}
            disabled={refecthLoading || inputValue?.length !== 6}
          >
            Continue
          </PrimaryButton>
        </Box>
      }
    >
      <Typography
        sx={{
          fontSize: { xs: 14, sm: 15 },
          mb: 3,
          textAlign: "center",
          color: "text.secondary",
          lineHeight: 1.6,
        }}
      >
        Please enter the verification code sent to{" "}
        <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
          {userInput}
        </Box>{" "}
        via SMS
      </Typography>
      <Box sx={{ mb: 3 }}>
        <CustomPinInput handleOnSubmit={onSubmit} setInputValue={setInputValue} />
      </Box>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? "primary.lighter"
              : alpha(theme.palette.primary.main, 0.1),
          border: "1px solid",
          borderColor: "primary.main",
        }}
      >
        <OtpResendButton
          cooldownHook={cooldownHook}
          onResend={() => setRequestOtp(true)}
          isLoading={refecthLoading}
          size="small"
          showMessage={true}
        />
      </Box>
    </AuthLayout>
  );
}
export default RequestOtp;
